import type { ComponentPropsWithoutRef, ReactElement } from 'react'
import { controlClassName, isAriaInvalid } from './control-classes.ts'

/**
 * A single-line text control. Intentionally almost nothing: `Field` owns
 * the label, the hint, the error and the `aria-describedby`, so this is a
 * native `<input>` wearing the shared box.
 *
 * Everything native passes straight through — `type`, `value`, `onChange`,
 * `placeholder`, `autoComplete`, `pattern`. No `className`: the token
 * layer only holds if a primitive cannot be overridden at the call site.
 */

export interface InputProps extends Omit<
  ComponentPropsWithoutRef<'input'>,
  'className'
> {
  /** For machine values — node paths, argv patterns, key names. Monospace
   * so two near-identical ids can be told apart by eye. */
  isMono?: boolean
}

export function Input({ isMono = false, ...rest }: InputProps): ReactElement {
  return (
    <input
      // `h-row` is --spacing-row, the same 32px as a table row: inputs in
      // a toolbar above a table have to line up with the rows below.
      className={`h-row ${controlClassName({
        isInvalid: isAriaInvalid(rest['aria-invalid']),
        isMono,
      })}`}
      {...rest}
    />
  )
}
